import { Box } from '@material-ui/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'

import countries from '../../../../i18n/data/countries'

import { Button, Checkbox, Flex, FilterList, SearchFilterList, DateRange } from '@percent/lemonade'
import styles from './DonationsFiltersAndActions.module.scss'
import { useQueryList } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { createShortLink } from '@percent/utility'
import { DonationsFiltersAndActionsProps } from './DonationsFiltersAndActions.types'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { DonationType } from '@percent/admin-dashboard/api/actions/donations/donations.types'

const THROTTLING_TIME = 500

type Option = {
  label: string
  value: string
}

const arraysEqual = (options1: Option[], options2: Option[]) =>
  options1.length === options2.length && options1.every((o, idx) => isEqual(o, options2[idx]))

const donationTypeOptions = [
  { value: DonationType.Hosted, label: 'typography.donationType.hosted' },
  { value: DonationType.Direct, label: 'typography.donationType.direct' },
  { value: DonationType.Wallet, label: 'typography.donationType.wallet' },
  { value: DonationType.Matched, label: 'typography.donationType.matched' },
  { value: DonationType.External, label: 'typography.donationType.external' },
  { value: DonationType.Payroll, label: 'typography.donationType.payroll' }
]

export function DonationsFiltersAndActions({
  queryParams,
  setQueryParams,
  handleExportCsvClick,
  handleImportCsvClick,
  handleOnHoldOnlyChange,
  isUploadInProgress
}: DonationsFiltersAndActionsProps) {
  const { t, i18n } = useTranslation()
  const { adminService } = useServices()
  const reassignDonationFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_REASSIGN_DONATION)

  const defaultOption = useMemo(
    () => ({
      label: t('status.all'),
      value: ''
    }),
    [t]
  )

  const [partnersOptions, setPartnersOptions] = useState([defaultOption])
  const [organisationOptionsFilter, setOrganisationOptionsFilter] = useState('')
  const [organisationsOptions, setOrganisationsOptions] = useState([defaultOption])
  const [partnerOptionsFilter, setPartnerOptionsFilter] = useState('')
  const [dropdownOrganisationId, setDropdownOrganisationId] = useState<string | undefined>(queryParams.organisationId)
  const [dropdownPartnerId, setDropdownPartnerId] = useState<string | undefined>(queryParams.partnerId)
  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)

  const [{ dataOrNull: partnersData, error: partnersError, isLoading: isPartnersLoading }, { query: partnersQuery }] =
    useQueryList(adminService.queries.getPartnerList, {
      pageSize: 25
    })

  const [
    { dataOrNull: organisationsData, error: organisationsError, isLoading: isOrganisationsLoading },
    { query: organisationsQuery }
  ] = useQueryList(adminService.getOrganisations, {
    pageSize: 25
  })

  const selectedPartnerOption = useMemo(() => {
    return partnersOptions.find(option => option.value === queryParams.partnerId) || partnersOptions[0]
  }, [partnersOptions, queryParams.partnerId])

  const selectedOrganisationOption = useMemo(() => {
    return organisationsOptions.find(option => option.value === queryParams.organisationId) || organisationsOptions[0]
  }, [organisationsOptions, queryParams.organisationId])

  const shortLanguage = i18n.language.split('-')[0]

  useEffect(() => {
    if (!isPartnersLoading && !partnersError && partnersData && (partnerOptionsFilter || queryParams.partnerId)) {
      const newOptions = partnersData.map(({ name, id }) => ({
        label: name,
        value: id
      }))

      const uniqueOptions = [defaultOption, ...newOptions].reduce(
        (accumulator: { label: string; value: string }[], current) => {
          if (!accumulator.find(item => item.value === current.value)) {
            accumulator.push(current)
          }

          return accumulator
        },
        []
      )

      if (selectedPartnerOption.value && !uniqueOptions.find(item => item.value === selectedPartnerOption.value)) {
        uniqueOptions.push(selectedPartnerOption)
      }

      if (!arraysEqual(uniqueOptions, partnersOptions)) {
        setPartnersOptions(uniqueOptions)
      }
    }
  }, [
    defaultOption,
    partnersData,
    partnersError,
    isPartnersLoading,
    partnerOptionsFilter,
    selectedPartnerOption,
    partnersOptions,
    queryParams.partnerId
  ])

  useEffect(() => {
    if (
      !isOrganisationsLoading &&
      !organisationsError &&
      organisationsData &&
      (organisationOptionsFilter || queryParams.organisationId)
    ) {
      const newOptions = organisationsData.map(({ name, id, registryId, website, countryCode }) => {
        const extras = [
          countries.getName(countryCode, shortLanguage),
          `${t('typography.id')}: ${registryId}`,
          ...(website ? [createShortLink(website)] : [])
        ]

        return {
          label: `${name} (${extras.join(', ')})`,
          value: id
        }
      })

      const uniqueOptions = [defaultOption, ...newOptions].reduce(
        (accumulator: { label: string; value: string }[], current) => {
          if (!accumulator.find(item => item.value === current.value)) {
            accumulator.push(current)
          }

          return accumulator
        },
        []
      )

      if (
        selectedOrganisationOption.value &&
        !uniqueOptions.find(item => item.value === selectedOrganisationOption.value)
      ) {
        uniqueOptions.push(selectedOrganisationOption)
      }

      if (!arraysEqual(uniqueOptions, organisationsOptions)) {
        setOrganisationsOptions(uniqueOptions)
      }
    }
  }, [
    defaultOption,
    organisationsData,
    organisationsError,
    isOrganisationsLoading,
    organisationOptionsFilter,
    selectedOrganisationOption,
    shortLanguage,
    t,
    organisationsOptions,
    queryParams.organisationId
  ])

  const debouncedPartnersQuery = useCallback(
    debounce<any>((filter: string) => {
      partnersQuery({ query: filter })
    }, THROTTLING_TIME),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  useEffect(() => {
    if (partnerOptionsFilter) {
      debouncedPartnersQuery(partnerOptionsFilter)
    }
  }, [partnerOptionsFilter, debouncedPartnersQuery])

  const debouncedOrganisationsQuery = useCallback(
    debounce<any>((filter: string) => {
      organisationsQuery({ query: filter })
    }, THROTTLING_TIME),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  useEffect(() => {
    if (organisationOptionsFilter) {
      debouncedOrganisationsQuery(organisationOptionsFilter)
    }
  }, [organisationOptionsFilter, debouncedOrganisationsQuery])

  useEffect(() => {
    if (queryParams.organisationId) {
      organisationsQuery({ query: queryParams.organisationId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (queryParams.partnerId) {
      partnersQuery({ partnerIds: [queryParams.partnerId] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setDropdownOrganisationId(queryParams.organisationId)
  }, [queryParams.organisationId])

  useEffect(() => {
    setDropdownPartnerId(queryParams.partnerId)
  }, [queryParams.partnerId])

  const handleOrganisationKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setOrganisationOptionsFilter((event.target as HTMLInputElement).value)
    }

    if (event.key === 'Escape') {
      setOrganisationOptionsFilter('')
    }
  }

  const handlePartnerKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setPartnerOptionsFilter((event.target as HTMLInputElement).value)
    }

    if (event.key === 'Escape') {
      setPartnerOptionsFilter('')
    }
  }

  const organisationFilterOptions = useMemo(
    () => organisationsOptions.filter(opt => opt.value !== ''),
    [organisationsOptions]
  )

  const partnerFilterOptions = useMemo(() => partnersOptions.filter(opt => opt.value !== ''), [partnersOptions])

  const handleDateRangeClick = useCallback(() => {
    setIsDatePickerOpened(open => !open)
  }, [])

  const dateRangeQueryParams = useMemo(() => {
    if (queryParams.startDate && queryParams.endDate) {
      return {
        startDate: queryParams.startDate,
        endDate: queryParams.endDate
      }
    }

    if (isDatePickerOpened) {
      const defaultStart = dayJS().format('YYYY-MM-DDTHH:mm:ssZ')
      const defaultEnd = dayJS().format('YYYY-MM-DDTHH:mm:ssZ')

      return {
        startDate: defaultStart,
        endDate: defaultEnd
      }
    }

    return {
      startDate: undefined,
      endDate: undefined
    }
  }, [isDatePickerOpened, queryParams.endDate, queryParams.startDate])

  return (
    <Box className={styles.wrapper}>
      <Flex gap={4} align="center" wrap="wrap">
        <SearchFilterList
          options={organisationFilterOptions}
          isLoading={isOrganisationsLoading}
          selectedValues={dropdownOrganisationId ? [dropdownOrganisationId] : []}
          onChange={values => {
            const newId = values.length > 0 ? values[values.length - 1] : undefined
            setDropdownOrganisationId(newId)

            if (values.length === 0) {
              setQueryParams({ ...queryParams, organisationId: undefined })
            }
          }}
          onButtonClick={() =>
            setQueryParams({
              ...queryParams,
              organisationId: dropdownOrganisationId
            })
          }
          searchValue={organisationOptionsFilter}
          onSearchChange={event => setOrganisationOptionsFilter(event.target.value)}
          handleClearValue={() => setOrganisationOptionsFilter('')}
          onKeyDown={handleOrganisationKeyDown}
          testId="donations-organisation-search-filter"
          buttonText={t('typography.search')}
          placeholder={t('typography.searchByOrganisationNameOrId')}
          label={t('dropdown.organisation')}
          placement="bottom"
          noResultsFoundText={
            organisationOptionsFilter.length < 3 ? t('typography.enterAtLeast3Characters') : undefined
          }
        />
        <SearchFilterList
          options={partnerFilterOptions}
          isLoading={isPartnersLoading}
          selectedValues={dropdownPartnerId ? [dropdownPartnerId] : []}
          onChange={values => {
            const newId = values.length > 0 ? values[values.length - 1] : undefined
            setDropdownPartnerId(newId)

            if (values.length === 0) {
              setQueryParams({ ...queryParams, partnerId: undefined })
            }
          }}
          onButtonClick={() =>
            setQueryParams({
              ...queryParams,
              partnerId: dropdownPartnerId
            })
          }
          searchValue={partnerOptionsFilter}
          onSearchChange={event => setPartnerOptionsFilter(event.target.value)}
          handleClearValue={() => setPartnerOptionsFilter('')}
          onKeyDown={handlePartnerKeyDown}
          testId="donations-partner-search-filter"
          buttonText={t('typography.search')}
          placeholder={t('typography.searchByPartnerName')}
          label={t('dropdown.partner')}
          placement="bottom"
          noResultsFoundText={partnerOptionsFilter.length < 3 ? t('typography.enterAtLeast3Characters') : undefined}
        />
      </Flex>

      <div className={styles.typeDropdown}>
        <FilterList
          label={t('dropdown.donationType')}
          options={donationTypeOptions.map(({ value, label }) => ({
            value,
            label: t(label as 'typography.donationType.hosted')
          }))}
          selectedValues={queryParams.kinds ?? []}
          onChange={newValues => {
            const withoutAll = newValues.filter(v => v !== '')
            setQueryParams({
              ...queryParams,
              kinds: withoutAll.length > 0 ? [withoutAll[withoutAll.length - 1]] : undefined
            })
          }}
          testId="donationTypeSelect"
          placement="bottom-start"
        />
      </div>

      <DateRange
        open={isDatePickerOpened}
        setOpen={setIsDatePickerOpened}
        queryParams={dateRangeQueryParams}
        setQueryParams={params => {
          if (typeof params === 'object') {
            setQueryParams?.({
              ...queryParams,
              startDate: params?.startDate,
              endDate: params?.endDate
            })
            setIsDatePickerOpened(false)
          }
        }}
        applyButtonText={t('button.apply')}
        withinPortal={false}
        maxDate={dayJS().endOf('day').toDate()}
        dropdownClassName={styles.dateRangeDropdown}
      >
        <div className={styles.dateRangeButtonWrapper}>
          <Button
            size="small"
            variant="tertiary"
            onClick={handleDateRangeClick}
            endIcon={isDatePickerOpened ? 'chevron-up' : 'chevron-down'}
            endIconSize={3}
          >
            {queryParams.startDate
              ? `${dayJS(queryParams.startDate).format('ll')} - ${dayJS(queryParams.endDate).format('ll')}`
              : t('dropdown.date')}
          </Button>
        </div>
      </DateRange>

      {reassignDonationFeatureFlag && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            name="onHoldOnly"
            label={t('checkbox.onHoldOnly')}
            active={!!queryParams.onHoldOnly}
            onChange={handleOnHoldOnlyChange}
            variant="default"
            dataTestId="onHoldOnlyCheckbox"
          />
        </div>
      )}

      {queryParams.startDate ||
      queryParams.endDate ||
      queryParams.organisationId ||
      queryParams.partnerId ||
      queryParams.onHoldOnly ||
      (queryParams.kinds && queryParams.kinds.length > 0) ? (
        <Button
          size="small"
          variant="tertiary"
          onClick={() => {
            setQueryParams({
              ...queryParams,
              partnerId: undefined,
              organisationId: undefined,
              startDate: undefined,
              endDate: undefined,
              onHoldOnly: undefined,
              kinds: undefined
            })
          }}
        >
          {t('button.clear')}
        </Button>
      ) : null}

      <Button
        size="small"
        endIcon="dropdown-arrow-down"
        onClick={handleImportCsvClick}
        disabled={isUploadInProgress}
        loading={isUploadInProgress}
      >
        {t('button.importCsv')}
      </Button>
      {reassignDonationFeatureFlag && (
        <Button
          size="small"
          endIcon="dropdown-arrow-down"
          onClick={handleExportCsvClick}
          disabled={isUploadInProgress}
          loading={isUploadInProgress}
        >
          {t('button.exportCsv')}
        </Button>
      )}
    </Box>
  )
}
